import React, { useEffect, useRef, useState } from 'react';

// Counts up once, only after the element scrolls into view, so the numbers feel
// alive without animating off-screen work on mount.
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Respect reduced-motion by jumping straight to the final value.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || done.current) return;
      done.current = true;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        // ease-out cubic
        setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return [value, ref];
}

export default function CountUpStat({ value, prefix = '', suffix = '' }) {
  const [shown, ref] = useCountUp(value);
  return (
    <span ref={ref}>
      {prefix}{shown}{suffix}
    </span>
  );
}
